import { useEffect, useState } from "react";
import { getAuditLog } from "../../library/allauth";
import { Search, Shield, Clock, User, ChevronDown, ChevronUp } from "lucide-react";

function timeAgo(dateStr) {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit"
  });
}

function getChangeType(message) {
  if (message.includes("Region")) return { label: "Region", color: "bg-blue-100 text-blue-700" };
  if (message.includes("ALPR") || message.includes("Analytics") || message.includes("Footage") || message.includes("Map"))
    return { label: "Feature", color: "bg-purple-100 text-purple-700" };
  if (message.includes("Role")) return { label: "Role", color: "bg-orange-100 text-orange-700" };
  if (message.includes("disabled") || message.includes("enabled"))
    return { label: "Account", color: "bg-red-100 text-red-700" };
  return { label: "Access", color: "bg-gray-100 text-gray-600" };
}

function LogRow({ log }) {
  const [expanded, setExpanded] = useState(false);
  const changeType = getChangeType(log.message);

  // Parse message lines
  const lines = log.message.split('\n').filter(l => l.trim());
  const summary = lines.find(l => l.startsWith('•')) || lines[0];
  const allChanges = lines.filter(l => l.startsWith('•'));

  return (
    <div className="border-b border-gray-50 last:border-0">
      <div
        className="flex items-center gap-4 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        {/* Time */}
        <div className="w-32 flex-shrink-0">
          <p className="text-xs text-gray-500">{timeAgo(log.created_at)}</p>
        </div>

        {/* Admin */}
        <div className="w-36 flex-shrink-0">
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
              <Shield size={10} className="text-purple-600" />
            </div>
            <p className="text-xs font-medium text-gray-700 truncate">{log.admin}</p>
          </div>
        </div>

        {/* User */}
        <div className="w-44 flex-shrink-0">
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
              <User size={10} className="text-gray-500" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-gray-700 truncate">{log.user}</p>
              {log.user_email && log.user_email !== '—' && (
                <p className="text-xs text-gray-400 truncate">{log.user_email}</p>
              )}
            </div>
          </div>
        </div>

        {/* Change summary */}
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-600 truncate">{summary}</p>
        </div>

        {/* Type badge */}
        <div className="w-20 flex-shrink-0">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${changeType.color}`}>
            {changeType.label}
          </span>
        </div>

        {/* Expand */}
        <div className="w-6 flex-shrink-0 flex justify-center">
          {allChanges.length > 1 ? (
            expanded
              ? <ChevronUp size={14} className="text-gray-400" />
              : <ChevronDown size={14} className="text-gray-400" />
          ) : null}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-4 pb-3 ml-32 bg-gray-50 border-t border-gray-100">
          <div className="pt-3 space-y-1">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              Full Details
            </p>
            {lines.map((line, i) => (
              <p key={i} className="text-xs text-gray-600 leading-relaxed">
                {line}
              </p>
            ))}
            <p className="text-xs text-gray-400 mt-2 pt-2 border-t border-gray-200">
              {new Date(log.created_at).toLocaleString("en-US", {
                weekday: "long", year: "numeric", month: "long",
                day: "numeric", hour: "2-digit", minute: "2-digit"
              })}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const fetchLogs = async (searchVal = "") => {
    setLoading(true);
    try {
      const params = {};
      if (searchVal) params.search = searchVal;
      const res = await getAuditLog(params);
      if (res.status === 200) setLogs(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    fetchLogs(searchInput);
  };

  return (
    <div className="max-w-6xl mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Audit Log</h1>
          <p className="text-sm text-gray-400 mt-0.5">
            History of all permission changes made by administrators
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <Clock size={13} />
          Last 100 entries
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
        <form onSubmit={handleSearch} className="flex gap-3">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by user, admin, or change type..."
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white bg-emerald-700 rounded-lg hover:bg-emerald-800 transition-colors"
          >
            Search
          </button>
          {search && (
            <button
              type="button"
              onClick={() => { setSearchInput(""); setSearch(""); fetchLogs(); }}
              className="px-4 py-2 text-sm text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Clear
            </button>
          )}
        </form>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {/* Column Headers */}
        <div className="flex items-center gap-4 px-4 py-2.5 bg-gray-50 border-b border-gray-100">
          <div className="w-32 flex-shrink-0">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Time</p>
          </div>
          <div className="w-36 flex-shrink-0">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Admin</p>
          </div>
          <div className="w-44 flex-shrink-0">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">User</p>
          </div>
          <div className="flex-1">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Change</p>
          </div>
          <div className="w-20 flex-shrink-0">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Type</p>
          </div>
          <div className="w-6 flex-shrink-0" />
        </div>

        {/* Rows */}
        {loading ? (
          <div className="p-8 text-center text-gray-400 text-sm">Loading audit log...</div>
        ) : logs.length === 0 ? (
          <div className="p-12 text-center">
            <Clock size={32} className="text-gray-200 mx-auto mb-3" />
            <p className="text-sm text-gray-400">No audit log entries yet</p>
            <p className="text-xs text-gray-300 mt-1">
              Changes will appear here when admin modifies user permissions
            </p>
          </div>
        ) : (
          logs.map((log) => <LogRow key={log.id} log={log} />)
        )}
      </div>

      {logs.length > 0 && (
        <p className="text-xs text-gray-400 text-center mt-3">
          Showing {logs.length} entries
        </p>
      )}
    </div>
  );
}